<template>
	<div class="category-item" :class="{deletable: itemQuantity > 0, noImage: !withImages}"  @click="counterStore.addCartItem(item)" :style="itemStyle">
		<div class="item-image" :style="'background-image: url('+(picture)+')'">
			<div class="item-quantity" :class="{visible: itemQuantity > 0}">{{ itemQuantity }}</div>
		</div>
		<div class="item-info">
			<div class="item-name-container">
				<div class="item-name">{{ item.name }}</div>
			</div>
			<div class="item-info-adv">
				<div class="quantity" v-if="!withImages" :class="{hide: itemQuantity <= 0}">{{ "x"+itemQuantity }}</div>
				<div class="price-container">
					<AvailabilityDot v-if="showAvailabilityType" :type="availabilityType" />
					<div class="item-price">{{ currencyStore.readableCurrencyValue(item.price) }}</div>
				</div>
			</div>
		</div>
		<div class="item-delete" @click.stop="counterStore.removeCartItem(item)">
			<img :src="'https://img.qromo.io/img/icons/icon-bin-counter-blue'+(userStore.darkMode ? '-dark' : '')+'.svg'">
		</div>
	</div>
</template>

<script>
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import {useLanguageStore, useCurrencyStore} from "@/shared/stores";
import {useMenuStore, useUserStore, useCounterStore, useWarehouseStore} from "@/console-new/stores";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let menuStore = useMenuStore();
		let counterStore = useCounterStore();
		const currencyStore = useCurrencyStore();
		let warehouseStore = useWarehouseStore();
		return {getString: languageStore.getString, userStore, currencyStore, warehouseStore, menuStore, counterStore};
	},
	name: "CounterCategoryItem",
	components: {AvailabilityDot},
	props: {
		item: {
			type: Object,
			required: true,
			validator: (list) => {
				return list.hasOwnProperty('name') && list.hasOwnProperty('price')
			}
		},
		taxonomy: {
			type: String,
			default: null
		},
		fallbackColor: {
			type: String,
			default: null
		},
	},
	computed: {
		withImages() {
			return this.userStore.currBusiness && this.userStore.currBusiness.counter_images;
		},
		itemQuantity() {
			return this.counterStore.getCartItemQuantity(this.item);
		},
		picture() {
			if(this.item.picture) {
				return this.item.picture;
			} else {
				if(this.taxonomy) {
					return URL_HTML+'/img/categories/'+this.taxonomy+'.php?c=0752a3&b=bfd0e4';
				} else {
					return URL_HTML+'/img/categories/icon-generic-food-drink-1.php?c=0752a3&b=bfd0e4';
				}
			}
		},
		itemStyle() {
			let baseColor = "#ffffff";
			let baseColorNoAlpha = baseColor;
			let baseColorIntermediate = baseColor;

			let alphaLow = "20";
			let alphaHigh = "60";
			let alphaIntermediate = "94";
			if(this.userStore.darkMode) {
				alphaLow = "30";
				alphaHigh = "75";
				alphaIntermediate = "b2";
			}

			if(this.item.color) {
				baseColor = "#"+this.item.color.replaceAll("#", "")+alphaLow;
				baseColorNoAlpha = "#"+this.item.color.replaceAll("#", "")+alphaIntermediate;
				baseColorIntermediate = "#"+this.item.color.replaceAll("#", "")+alphaHigh;
			} else if(this.fallbackColor && this.fallbackColor) {
				baseColor = "#"+this.fallbackColor.replaceAll("#", "")+alphaLow;
				baseColorNoAlpha = "#"+this.fallbackColor.replaceAll("#", "")+alphaIntermediate;
				baseColorIntermediate = "#"+this.fallbackColor.replaceAll("#", "")+alphaHigh;
			}

			return {
				'--item-color': baseColor,
				'--item-color-no-alpha': baseColorNoAlpha,
				'--item-color-intermediate': baseColorIntermediate,
			};
		},
		isInWarehouse() {
			return this.item?.warehouse && this.item?.sku;
		},
		showAvailabilityType() {
			return !this.item.available || (this.isInWarehouse && this.warehouseStock <= this.warehouseThreshold);
		},
		availabilityType() {
			if(this.isInWarehouse && this.warehouseStock <= this.warehouseThreshold) {
				return 1;
			}
			if(this.item.available === false) {
				return 1;
			}
			return 2;
		},
		warehouseThreshold() {
			if(this.isInWarehouse) {
				let warehouseItem = this.warehouseStore.warehouseItemsSKUs[this.item.sku];
				if(warehouseItem) {
					return Number(warehouseItem.affects_availability || 0);
				}
			}
			return 0;
		},
		warehouseStock() {
			if(this.isInWarehouse) {
				let warehouseItem = this.warehouseStore.warehouseItemsSKUs[this.item.sku];
				if(warehouseItem) {
					return Number(warehouseItem.stock || 0);
				}
			}
			return 0;
		}
	},
}
</script>

<style scoped lang="scss">
.category-item {
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 6px;
	box-shadow: 0 2px 3px var(--color-box-shadow-3);
	border-radius: 13px;
	min-width: 160px;
	height: 76px;
	overflow: hidden;
	--num-items-row: 8;
	width: calc((100% / var(--num-items-row)) - ((var(--items-gap) * (var(--num-items-row) - 1) ) / var(--num-items-row)));
	cursor: pointer;
	@include noSelect;
	background-color: var(--item-color);
	transition: background-color 0.2s $transition;

	@media (max-width: 2100px) {
		--num-items-row: 7;
	}

	@media (max-width: 1895px) {
		--num-items-row: 6;
	}

	@media (max-width: 1695px) {
		--num-items-row: 5;
	}

	@media (max-width: 1490px) {
		--num-items-row: 4;
	}

	@media (max-width: 1220px) {
		--num-items-row: 3;
	}

	@media (max-width: 990px) {
		--num-items-row: 2;
	}

	@media (max-width: 750px) {
		--num-items-row: 3;
	}

	@media (max-width: 600px) {
		--num-items-row: 2;
	}

	@media (max-width: 360px) {
		--num-items-row: 1;
	}

	.item-image {
		width: 56px;
		height: 56px;
		border-radius: 13px;
		box-shadow: inset 1px 2px 3px var(--color-box-shadow-3);
		background-size: cover;
		background-position: center;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: var(--color-bg-item);

		.item-quantity {
			width: 80%;
			height: 80%;
			background-color: var(--color-primary-alpha-5);
			backdrop-filter: blur(3px);
			-webkit-backdrop-filter: blur(3px);
			border-radius: 13px;
			color: var(--color-bg);
			font-size: 16px;
			font-weight: 700;
			display: flex;
			justify-content: center;
			align-items: center;
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
			overflow: hidden;
			@include noSelect();

			&.visible {
				opacity: 1;
			}
		}
	}


	.item-info {
		display: flex;
		flex-direction: column;
		margin-left: 6px;
		width: calc(100% - 56px);
		justify-content: space-between;
		height: 100%;
		transition: width 0.2s $transition;
		@include noSelect();

		.item-name-container {
			display: flex;
			align-items: flex-start;
			justify-content: flex-start;
			width: 100%;
			margin-bottom: 5px;
			min-height: 44px;

			.item-name {
				line-height: 18px;
				font-size: 16px;
				font-weight: 500;
				width: 100%;
				@include truncateString(2);

				@media (max-width: 420px) {
					font-size: 14px;
				}
			}

		}

		.item-info-adv {
			display: flex;
			align-items: center;
			justify-content: space-between;

			.quantity {
				line-height: 15px;
				font-size: 15px;
				font-weight: 500;
				color: var(--color-primary);
				max-width: 80px;
				transition: max-width 0.3s $transition;
				overflow: hidden;
				display: inline-block;
				&.hide {
					max-width: 0;
				}
			}

			.price-container {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				gap: 5px;

				.item-price {
					text-align: right;
					font-size: 13px;
					line-height: 15px;
					color: var(--color-txt-1);
					padding-right: 4px;
				}
			}

		}
	}

	@media (max-width: 420px) {

		.item-image {
			width: 40px;
			height: 40px;
		}

		.item-info {
			width: calc(100% - 46px);

			.item-name {
				white-space: nowrap;
				display: block;
			}
		}
	}

	.item-delete {
		width: 0;
		height: 100%;
		transition: width 0.3s $transition, opacity 0.3s $transition;
		overflow: hidden;
		display: flex;
		justify-content: center;
		align-items: center;
		opacity: 0;

		img {
			width: 100%;
			padding: 3px;
		}
	}

	&.deletable {
		box-shadow: 0 1px 3px 1px var(--item-color-no-alpha) inset;
		background-color: var(--item-color-intermediate);
		.item-info {
			width: calc(100% - 76px);
		}

		.item-delete {
			width: 20px;
			opacity: 1;
		}

		@media (max-width: 420px) {
			.item-info {
				width: calc(100% - 66px);
			}
		}
	}


	&.noImage {
		.item-image {
			display: none;
		}

		.item-info {
			width: 100%;
			margin-left: 2px;
			margin-right: 2px;

			.item-name-container {
				.item-name {
					font-size: 18px;
					line-height: 22px;
				}
			}
		}
	}
}
</style>